'use client';

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ProductsApi, Product } from '@/lib/Apis';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';

interface UseWishlistProductsResult {
  products: Product[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetches full product details for a given list of wishlist product IDs.
 * Uses React Query's useQueries for parallel fetching and caching.
 */
export function useWishlistProducts(productIds: string[] = []): UseWishlistProductsResult {
  const uniqueIds = useMemo(
    () => Array.from(new Set((productIds || []).filter(Boolean))),
    [productIds]
  );

  const results = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ['product', typeof id === 'object' ? (id as any)?._id : id],
      queryFn: async () => {
        const productId = typeof id === 'object' ? (id as any)?._id : id;
        const response = await new ProductsApi(AXIOS_CONFIG).getSingleProduct(productId);
        return response.data;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes - shares cache with useProduct
      enabled: !!id,
    })),
  });

  const isLoading = results.some((result) => result.isLoading);
  const error = results.find((result) => result.error)?.error as Error | null;
  const products = results
    .filter((result) => result.data)
    .map((result) => result.data as Product);

  const refetch = () => {
    results.forEach((result) => result.refetch());
  };

  return {
    products,
    isLoading,
    error,
    refetch,
  };
}
